// Client-side image optimization.
//
// Recipe photos come straight from a phone camera (JPEG/HEIC, often 12 MB).
// Serving those originals to a phone is the app's biggest first-load cost, so
// we downscale + transcode to WebP *in the browser* before upload — no paid
// Cloudflare Image Resizing, negligible R2 storage. The browser decodes the
// camera file and re-encodes each variant via canvas.toBlob('image/webp').
//
// We never upscale: a variant's pixel width is capped at the source width, but
// it keeps its target label (400/800/1600) so the stored key and the srcset on
// the read side stay predictable. See photoSrcSet() in lib/api.ts.

export const PHOTO_WIDTHS = [400, 800, 1600] as const;
const WEBP_QUALITY = 0.82;

export interface PhotoVariant {
  width: number; // the target label (400/800/1600), not necessarily the pixel width
  blob: Blob;
}

export async function buildPhotoVariants(file: File): Promise<PhotoVariant[]> {
  const bitmap = await createImageBitmap(file);
  try {
    const variants: PhotoVariant[] = [];
    for (const target of PHOTO_WIDTHS) {
      const outW = Math.min(target, bitmap.width);
      const scale = outW / bitmap.width;
      const outH = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Prohlížeč nepodporuje úpravu obrázků (canvas).');
      ctx.drawImage(bitmap, 0, 0, outW, outH);

      variants.push({ width: target, blob: await canvasToWebp(canvas) });
    }
    return variants;
  } finally {
    bitmap.close();
  }
}

function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Konverze obrázku do WebP selhala.'))),
      'image/webp',
      WEBP_QUALITY,
    );
  });
}
